import React, { forwardRef } from 'react'
import { Box, Divider, MenuItem, MenuList, Typography } from '@mui/material'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye'
import StarIcon from '@mui/icons-material/Star'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { GridColumnMenuProps, GridPreferencePanelsValue, useGridApiContext, useGridRootProps } from '@mui/x-data-grid'

const menuListSx = {
	minWidth: 248,
	padding: '8px 0',
}

const menuItemSx = {
	padding: '6px 16px',
	minHeight: '36px',
	display: 'flex',
	alignItems: 'center',
}

const menuIconSlotSx = {
	minWidth: '36px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	color: 'var(--primary)',
}

const menuIconSx = {
	fontSize: 24,
}

const menuTextSx = {
	fontFamily: 'var(font-family)',
	fontWeight: 500,
	fontSize: '12px',
	lineHeight: 1.5,
	color: 'var(--primary-dark)',
}

const menuDividerSx = {
	margin: '8px 0',
	borderColor: 'rgba(134, 149, 175, 0.35)',
}

export const columnMenuPopperSx = {
	zIndex: 2100,
	'& .MuiPaper-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		boxShadow:
			'0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
		overflow: 'hidden',
		zIndex: 2100,
	},
}

type PinningApi = {
	setPinnedColumns?: (model: { left?: string[]; right?: string[] }) => void
	getPinnedColumns?: () => { left?: string[]; right?: string[] }
}

const CustomColumnMenu = forwardRef<HTMLUListElement, GridColumnMenuProps>((props, ref) => {
	const { hideMenu, colDef, open, id, labelledby, className, ...other } = props
	const apiRef = useGridApiContext()
	const rootProps = useGridRootProps()
	const sortingOrder = colDef.sortingOrder ?? rootProps.sortingOrder ?? []
	const canSort =
		!rootProps.disableColumnSorting &&
		colDef.sortable !== false &&
		sortingOrder.some(direction => direction === 'asc' || direction === 'desc')
	const canFilter = !rootProps.disableColumnFilter && colDef.filterable !== false
	const canHideColumn = !rootProps.disableColumnSelector && colDef.hideable !== false
	const canManageColumns = !rootProps.disableColumnSelector
	const visibleColumns = apiRef.current.getVisibleColumns()
	const columnsWithMenu = visibleColumns.filter(column => column.disableColumnMenu !== true)
	const hideDisabled = columnsWithMenu.length <= 1
	const pinningApi = apiRef.current as PinningApi
	const canPinColumn = typeof pinningApi.setPinnedColumns === 'function'

	const handleListKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Tab') {
			event.preventDefault()
		}
		if (event.key === 'Tab' || event.key === 'Escape') {
			hideMenu(event)
		}
	}

	const handleSort = (direction: 'asc' | 'desc') => (event: React.MouseEvent<HTMLElement>) => {
		hideMenu(event)
		apiRef.current.sortColumn(colDef.field, direction)
	}

	const handleFilter = (event: React.MouseEvent<HTMLElement>) => {
		hideMenu(event)
		apiRef.current.showFilterPanel(colDef.field)
	}

	const handleHideColumn = (event: React.MouseEvent<HTMLElement>) => {
		if (hideDisabled) {
			return
		}
		apiRef.current.setColumnVisibility(colDef.field, false)
		hideMenu(event)
	}

	const handleManageColumns = (event: React.MouseEvent<HTMLElement>) => {
		hideMenu(event)
		apiRef.current.showPreferences(GridPreferencePanelsValue.columns)
	}

	const handlePinColumn = (event: React.MouseEvent<HTMLElement>) => {
		if (!canPinColumn) {
			return
		}
		const currentPinned = pinningApi.getPinnedColumns?.() ?? {}
		const left = currentPinned.left ?? []
		const right = currentPinned.right ?? []
		const isPinned = left.includes(colDef.field) || right.includes(colDef.field)
		const nextLeft = isPinned ? left.filter(field => field !== colDef.field) : [...left, colDef.field]
		pinningApi.setPinnedColumns?.({ left: nextLeft, right })
		hideMenu(event)
	}

	const showSortGroup = canSort
	const showFilterGroup = canFilter
	const showHideGroup = canHideColumn
	const showPinGroup = true
	const showManageGroup = canManageColumns

	const showDividerAfterSort = showSortGroup && (showFilterGroup || showHideGroup || showPinGroup || showManageGroup)
	const showDividerAfterFilter = showFilterGroup && (showHideGroup || showPinGroup || showManageGroup)
	const showDividerAfterHide = (showHideGroup || showPinGroup) && showManageGroup

	return (
		<MenuList
			ref={ref}
			id={id}
			aria-labelledby={labelledby}
			autoFocus={open}
			onKeyDown={handleListKeyDown}
			className={className}
			sx={menuListSx}
			{...other}
		>
			{showSortGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handleSort('asc')}
				>
					<Box sx={menuIconSlotSx}>
						<ArrowUpwardIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Ordenar por Ascendente</Typography>
				</MenuItem>
			)}
			{showSortGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handleSort('desc')}
				>
					<Box sx={menuIconSlotSx}>
						<ArrowDownwardIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Ordenar por Descendente</Typography>
				</MenuItem>
			)}
			{showDividerAfterSort && <Divider sx={menuDividerSx} />}
			{showFilterGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handleFilter}
				>
					<Box sx={menuIconSlotSx}>
						<FilterAltIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Filtrar</Typography>
				</MenuItem>
			)}
			{showDividerAfterFilter && <Divider sx={menuDividerSx} />}
			{showHideGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handleHideColumn}
					disabled={hideDisabled}
				>
					<Box sx={menuIconSlotSx}>
						<RemoveRedEyeIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Ocultar Coluna</Typography>
				</MenuItem>
			)}
			{showPinGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handlePinColumn}
					disabled={!canPinColumn}
				>
					<Box sx={menuIconSlotSx}>
						<StarIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Fixar Coluna</Typography>
				</MenuItem>
			)}
			{showDividerAfterHide && <Divider sx={menuDividerSx} />}
			{showManageGroup && (
				<MenuItem
					sx={menuItemSx}
					onClick={handleManageColumns}
				>
					<Box sx={menuIconSlotSx}>
						<ViewColumnIcon sx={menuIconSx} />
					</Box>
					<Typography sx={menuTextSx}>Gerir Colunas</Typography>
				</MenuItem>
			)}
		</MenuList>
	)
})

CustomColumnMenu.displayName = 'CustomColumnMenu'

export default CustomColumnMenu
